import { BmsConfig, Box, Licenses, Report, Tenants, Users } from "@bcc-ui/ui-components";
import { t } from "i18next";
import React from "react";
import { useLocation } from "react-router-dom";

import style from "./settings-sub-menu.scss";
import { Folder } from "components/icons/Folder";
import { Wrench } from "components/icons/Wrench";
import { useIsChildRouteEnabled } from "components/navigation/hooks/useIsChildRouteEnabled";
import { useIsRouteEnabled } from "components/navigation/hooks/useIsRouteEnabled";
import { NavigationSubMenuItem } from "components/navigation/navigation-panel/sub-navigation-panel/contents/navigation-sub-menu-item/NavigationSubMenuItem";
import { NavigationSubSection } from "components/navigation/navigation-panel/sub-navigation-panel/ui-components/navigation-sub-section/NavigationSubSection";
import {
    AUDIT_LOGS_ROUTE,
    CONFIGURATION_BMS_LICENSES_ROUTE,
    FILE_SHARING_ROUTE,
    GENERAL_SETTINGS_ROUTE,
    LICENSES_ROUTE,
    RouteDefinition,
    TENANTS_ALL_TENANTS_ROUTE,
    USERS_ROUTE,
} from "components/router/Routes";
import { resolveColor } from "utils/commonFunctions";

interface Props {
    mainRoute: RouteDefinition;
    enteredFromMainNavigation: boolean;
    openChildRoutePaths: readonly string[];
    resetChildRoutePaths: () => void;
    onToggleChildRoute: (path: string) => void;
}

export const SettingsSubmenu = (props: Props) => {
    const isRouteEnabled = useIsRouteEnabled();
    const { isChildRouteEnabled, isChildRouteAdvertised } = useIsChildRouteEnabled();
    const location = useLocation();

    React.useEffect(() => {
        if (props.enteredFromMainNavigation) {
            props.resetChildRoutePaths();
            props.mainRoute.getAncestorPathsForTargetPath(location.pathname)?.forEach(props.onToggleChildRoute);
            return;
        }

        const ancestorPaths = props.mainRoute.getAncestorPathsForTargetPath(location.pathname);

        if (!ancestorPaths) {
            return;
        }

        const pathsToOpen = ancestorPaths.filter((path) => !props.openChildRoutePaths.includes(path));
        pathsToOpen.forEach(props.onToggleChildRoute);
    }, [location.pathname, props.enteredFromMainNavigation]);

    if (!props.mainRoute || !isRouteEnabled(props.mainRoute)) {
        return null;
    }

    const getIconForRoute = (route: RouteDefinition) => {
        const size = 20;
        const color = resolveColor("--theme-text-primary");

        switch (route) {
            case GENERAL_SETTINGS_ROUTE:
                return <Wrench size={size} color={"var(--theme-text-primary)"} />;
            case TENANTS_ALL_TENANTS_ROUTE:
                return <Tenants size={size} color={color} />;
            case LICENSES_ROUTE:
                return <Licenses size={size} color={color} />;
            case USERS_ROUTE:
                return <Users size={size} color={color} />;
            case FILE_SHARING_ROUTE:
                return <Folder size={size} color={"var(--theme-text-primary)"} />;
            case AUDIT_LOGS_ROUTE:
                return <Report size={size} color={color} />;
            case CONFIGURATION_BMS_LICENSES_ROUTE:
                return <BmsConfig size={size} color={color} />;
            default:
                return undefined;
        }
    };

    const renderChildRoutes = (routes: readonly RouteDefinition[]) => {
        return (
            <>
                {routes.map((childRoute) => {
                    if (!isChildRouteEnabled(childRoute)) {
                        return null;
                    }

                    const hasChildRoutes = childRoute.childRoutes.length > 0;

                    const isExpanded = hasChildRoutes && props.openChildRoutePaths.includes(childRoute.path);

                    return (
                        <Box columnLayout key={childRoute.path}>
                            <NavigationSubMenuItem
                                asBox={hasChildRoutes}
                                to={hasChildRoutes ? "" : childRoute.path}
                                onClick={hasChildRoutes ? () => props.onToggleChildRoute(childRoute.path) : undefined}
                                isAdvertised={isChildRouteAdvertised(childRoute)}
                                leftSection={getIconForRoute(childRoute)}
                                className={hasChildRoutes ? style.pointerCursor : undefined}
                                dataAttributes={{ ariaExpanded: isExpanded }}
                                testId={childRoute.testId}
                            >
                                {t(childRoute.title)}
                            </NavigationSubMenuItem>

                            {isExpanded && (
                                <NavigationSubSection>{renderChildRoutes(childRoute.childRoutes)}</NavigationSubSection>
                            )}
                        </Box>
                    );
                })}
            </>
        );
    };

    return <>{renderChildRoutes(props.mainRoute.childRoutes)}</>;
};
